import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Input } from "components/input";
import { useSearchArticles } from "api/use-intercom";
import { cn } from "lib/utils";

const SearchDropdown = ({ className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState("bottom");
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);

  const { data: searchResults, isLoading } = useSearchArticles(searchTerm);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Calculate dropdown position based on viewport space
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 500; // max-height

      // If there's more space above and not enough space below, position above
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
  }, [isOpen]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(value.trim().length > 0);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim().length > 0) {
      setIsOpen(true);
    }
  };

  const handleResultClick = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const articles = searchResults?.data?.articles || [];
  const hasResults = articles.length > 0;

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <Input
        type="text"
        placeholder="Search for help articles..."
        value={searchTerm}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        className="w-full"
        inputClassName="pr-10"
      />

      {/* Search icon */}
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
        <span className="material-symbols-outlined text-tertiary mt-[0.4rem]">
          search
        </span>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className={cn(
            "absolute left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-[500px] overflow-y-auto",
            {
              "top-full mt-1": dropdownPosition === "bottom",
              "bottom-full mb-1": dropdownPosition === "top",
            }
          )}
        >
          {isLoading ? (
            <div className="p-4 text-center text-tertiary flex items-center justify-center gap-2">
              <span className="material-symbols-outlined animate-spin">
                refresh
              </span>
              <span className="ml-2">Searching...</span>
            </div>
          ) : hasResults ? (
            <div className="py-2">
              {articles.map((article) => (
                <Link
                  key={article.id}
                  to={`/en-US/help/articles/${article.id}`}
                  onClick={handleResultClick}
                  className="block px-4 py-3 hover:bg-blue-300/25 border-b border-gray-300/25 last:border-b-0"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      <span className="material-symbols-outlined text-tertiary text-sm">
                        article
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-secondary line-clamp-2 mb-1">
                        {article.title}
                      </h3>
                      {article.description && (
                        <p className="text-sm text-tertiary line-clamp-2">
                          {article.description}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : searchTerm.trim().length > 0 ? (
            <div className="p-4 text-center text-tertiary">
              <span className="material-symbols-outlined">search_off</span>
              <p className="mt-2">No articles found for "{searchTerm}"</p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default SearchDropdown;
